import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { apiClient } from '@lib/api';

const Analytics: React.FC = () => {
  const { data: analytics } = useQuery({
    queryKey: ['analytics-summary'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/api/analytics');
      return response;
    },
  });

  const totals = analytics?.totals || { revenue_cents: 0, customers: 0, invoices: 0, linked_installs: 0 };
  const revenueData = (analytics?.revenueByMonth || []).map((r: any) => ({
    month: r.label,
    revenue: r.revenue_cents / 100,
  }));
  const customersData = (analytics?.customersByMonth || []).map((r: any) => ({
    month: r.label,
    customers: r.customers,
  }));

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Analytics</h1>

      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="text-sm text-gray-600 mb-2">Total Revenue</div>
          <div className="text-3xl font-bold text-gray-900">${(totals.revenue_cents / 100).toFixed(2)}</div>
          <div className="text-xs text-gray-500 mt-2">Paid invoices</div>
        </div>
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="text-sm text-gray-600 mb-2">Total Customers</div>
          <div className="text-3xl font-bold text-gray-900">{totals.customers}</div>
          <div className="text-xs text-gray-500 mt-2">All time</div>
        </div>
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="text-sm text-gray-600 mb-2">Linked Installs</div>
          <div className="text-3xl font-bold text-gray-900">{totals.linked_installs}</div>
          <div className="text-xs text-gray-500 mt-2">Under management</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#3b82f6" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Growth</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={customersData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="customers" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
